import { type FormEvent, useEffect, useState } from "react";
import { Input, Button, FormField, Wordmark } from "@praesto/ui-kit";
import { confirmReset, verifyResetCode } from "../lib/auth-actions";

/**
 * Password-reset action page. Rendered standalone (no auth) when the app is
 * opened on an Identity Platform email-action URL
 * (`?mode=resetPassword&oobCode=…&tenantId=…`). It verifies the code, lets the
 * user choose a new password, and confirms the reset.
 *
 * The Identity Platform "action URL" must point at this app for the email link
 * to land here (see docs) — the app intercepts the action by query params, not
 * by a specific path.
 */
export function ResetPasswordView({
  tenantId,
  tenantSlug,
  oobCode,
}: {
  tenantId: string;
  tenantSlug: string;
  oobCode: string;
}) {
  const [phase, setPhase] = useState<"verifying" | "form" | "done" | "invalid">("verifying");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    verifyResetCode(tenantId, oobCode)
      .then((mail) => {
        if (cancelled) return;
        setEmail(mail);
        setPhase("form");
      })
      .catch(() => {
        if (!cancelled) setPhase("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, oobCode]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Le due password non coincidono.");
      return;
    }
    setBusy(true);
    try {
      await confirmReset(tenantId, oobCode, password);
      setPhase("done");
    } catch {
      setError("Password troppo debole o link scaduto. Richiedi un nuovo link dal login.");
      setBusy(false);
    }
  }

  function goToLogin() {
    // No tenant (a project-level link on `/auth/action`) → the Access Hub; never
    // `//login`, which the browser reads as a host.
    window.location.assign(tenantSlug ? `/${encodeURIComponent(tenantSlug)}/login` : "/");
  }

  return (
    <div className="min-h-screen bg-canvas text-fg-default flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        {/* The brand arrives as a file (C3F v18) — the mono "PRAESTO · slug"
            header was the pre-v18 identity. */}
        <Wordmark height={34} />

        {phase === "verifying" && (
          <p className="mt-6 text-caption text-fg-muted">Verifica del link…</p>
        )}

        {phase === "invalid" && (
          <>
            <h1 className="mt-6 text-h3 font-semibold text-fg-strong">Link non valido</h1>
            <p className="mt-4 text-sm text-fg-muted leading-relaxed">
              Questo link di reset è scaduto o è già stato usato. Richiedine uno nuovo dalla pagina
              di login.
            </p>
            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              className="mt-8"
              onClick={goToLogin}
            >
              Vai al login
            </Button>
          </>
        )}

        {phase === "done" && (
          <>
            <h1 className="mt-6 text-h3 font-semibold text-fg-strong">Password aggiornata</h1>
            <p className="mt-4 text-sm text-fg-muted leading-relaxed">
              Ora puoi accedere con la nuova password.
            </p>
            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              className="mt-8"
              onClick={goToLogin}
            >
              Vai al login
            </Button>
          </>
        )}

        {phase === "form" && (
          <form onSubmit={submit}>
            <h1 className="mt-6 text-h3 font-semibold text-fg-strong">Nuova password</h1>
            <p className="mt-3 text-sm text-fg-muted leading-relaxed">
              Account: <span className="text-fg-default">{email}</span>
            </p>
            <div className="mt-6">
              <FormField label="Nuova password (min 12 caratteri)">
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </FormField>
            </div>
            <div className="mt-4">
              <FormField label="Conferma password">
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </FormField>
            </div>
            {error && (
              <p className="mt-4 text-sm text-danger" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy || password.length < 12}
              variant="primary"
              size="lg"
              fullWidth
              className="mt-8"
            >
              {busy ? "Aggiorno…" : "Imposta nuova password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
